import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../reducers';
import { Label, Button, ButtonsList, ResourceType } from './styles';
import { Api } from '../../typings/api';
import FromApp from '../../containers/ResourceSelector/FromApp';
import ImportManager from '../../containers/ImportManager';
import ProgressBar from './ProgressBar';
import YoutubeLinkPanel from '../Editor/EditPanel/YoutubeLinkPanel';
import { FileError, removeFileFromImport } from '../../containers/ImportManager/actions';

interface ResourceSelectorProps {
	id: string;
	label?: string;
	isValidResourceFromApp: (item: Api.DriveItem) => boolean;
	isOptimizedImageImport?: boolean;
	fileExtensions: string[];
	isFileExtensionsDisplayed: boolean;
	hasExternalLink?: boolean;
	externalLink?: string;
	isImportingFile: boolean;
	importingPercentage: number;
	importingError: FileError | null;
	isProcessingVideo: boolean;
	onSelect: (item: Api.DriveItem) => void;
	customImportHandler?: (file: File, isCorrectFileTypes: boolean) => void;
	onFileImportCancel?: () => void;
	onExternalLinkChange?: (value: string) => void;
	actionButtonColor?: string;
	dispatch: Dispatch<StoreState>;
}

enum ButtonTypes {
	desktop = 'from_desktop',
	app = 'from_app',
	web = 'from_web',
}

interface ButtonsConfig {
	[k: string]: {
		label: ButtonTypes;
		picker?: React.ReactNode;
		component?: React.ReactNode;
		onClick?: () => void;
	};
}

const ResourceSelector = ({
	id,
	label,
	isValidResourceFromApp,
	isOptimizedImageImport,
	fileExtensions,
	isFileExtensionsDisplayed,
	hasExternalLink,
	externalLink,
	isImportingFile,
	importingPercentage,
	importingError,
	isProcessingVideo,
	onSelect,
	customImportHandler,
	onFileImportCancel,
	onExternalLinkChange,
	actionButtonColor,
	dispatch,
}: ResourceSelectorProps) => {
	const [activePicker, setActivePicker] = React.useState('');

	const handleSelect = (item: Api.DriveItem) => {
		if (importingError) {
			handleFileImportCancel();
		}
		onSelect(item);
	};

	const handleExternalLinkChange = (value: string) => {
		if (onExternalLinkChange) {
			if (importingError) {
				handleFileImportCancel();
			}

			onExternalLinkChange(value);
		}
	};

	const handleFileImportCancel = () => {
		if (onFileImportCancel) {
			onFileImportCancel();
		} else {
			dispatch(removeFileFromImport(id));
		}
	};

	const buttonsConfig: ButtonsConfig = {
		[ButtonTypes.desktop]: {
			label: ButtonTypes.desktop,
			component: (
				<ImportManager
					id={id}
					triggerComponent={
						<Button as="div">
							<Translate value="messages.from_desktop" />
						</Button>
					}
					extensions={fileExtensions.join(', ')}
					onImport={(item) => customImportHandler ? undefined : handleSelect(item)}
					customImportHandler={customImportHandler}
					isOptimizedImageImport={isOptimizedImageImport}
				/>
			),
		},
		[ButtonTypes.app]: {
			label: ButtonTypes.app,
			picker: (
				<FromApp
					isValidResource={isValidResourceFromApp}
					onSelect={handleSelect}
					onClose={() => setActivePicker('')}
					actionButtonColor={actionButtonColor}
				/>
			),
			onClick: () => setActivePicker(ButtonTypes.app),
		},
	};

	if (hasExternalLink) {
		buttonsConfig[ButtonTypes.web] = {
			label: ButtonTypes.web,
			picker: (
				<YoutubeLinkPanel
					value={externalLink}
					onChange={handleExternalLinkChange}
				/>
			),
			onClick: () => setActivePicker(ButtonTypes.web),
		};
	}

	if (isImportingFile && !importingError) {
		return (
			<ProgressBar
				percentage={importingPercentage}
				error={importingError}
				isProcessing={isProcessingVideo}
				processingLabel="messages.video_processing"
				hasCancelButton={true}
				onCancel={handleFileImportCancel}
			/>
		);
	}

	return (
		<>
			{activePicker
				? <>{buttonsConfig[activePicker].picker}</>
				: (
					<>
						{isImportingFile && importingError &&
							<ProgressBar
								percentage={importingPercentage}
								error={importingError}
								isProcessing={isProcessingVideo}
								processingLabel="messages.video_processing"
								hasCancelButton={true}
								onCancel={handleFileImportCancel}
							/>
						}
						{label && <Label value={label} />}
						<ButtonsList>
							{Object.values(buttonsConfig).map(({ label, component, onClick }) => (
								<li key={label}>
									{component || (
										<Button
											type="button"
											onClick={onClick}
										>
											<Translate value={`messages.${label}`} />
										</Button>
									)}
								</li>
							))}
						</ButtonsList>
						{isFileExtensionsDisplayed &&
							<ResourceType>
								<Translate value="messages.type" />
								{`: ${fileExtensions.map(item => item.substring(1)).join(', ')}`}
							</ResourceType>
						}
					</>
				)
			}
		</>
	);
};

export default ResourceSelector;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ResourceSelector/index.tsx